
import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";

// Constants for rate limiting
const COOLDOWN_MINUTES = 5;
const DAILY_LIMIT = 5;

interface RateLimitStorageData {
  lastSentAt: number;
  dailySendCount: number;
  lastSentDate: string;
}

export function useResendRateLimit(emailType: 'verification' | 'reset') {
  const { t } = useLanguage();
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(DAILY_LIMIT);
  const [isLimitReached, setIsLimitReached] = useState(false);
  
  const storageKey = `email_${emailType}_ratelimit`;

  // Load rate limit data from localStorage and update state
  const loadRateLimitData = () => {
    try {
      const storedData = localStorage.getItem(storageKey);
      if (!storedData) {
        setRemainingAttempts(DAILY_LIMIT);
        return;
      }
      
      const data: RateLimitStorageData = JSON.parse(storedData);
      const currentDate = new Date().toISOString().split('T')[0];
      
      // Reset counter if it's a new day
      if (data.lastSentDate !== currentDate) {
        setRemainingAttempts(DAILY_LIMIT);
        setIsLimitReached(false);
        return;
      }
      
      // Calculate remaining time in cooldown
      const now = Date.now();
      const elapsedMs = now - data.lastSentAt;
      const cooldownMs = COOLDOWN_MINUTES * 60 * 1000;
      const remainingMs = Math.max(0, cooldownMs - elapsedMs);
      
      // Update state based on remaining time
      setRemainingTime(Math.ceil(remainingMs / 1000));
      setIsRateLimited(remainingMs > 0);
      
      // Update daily limit state
      const attemptsRemaining = DAILY_LIMIT - data.dailySendCount;
      setRemainingAttempts(attemptsRemaining);
      setIsLimitReached(attemptsRemaining <= 0);
    } catch (error) {
      console.error("Error loading rate limit data:", error);
      // Reset on error
      setRemainingAttempts(DAILY_LIMIT);
      setIsLimitReached(false);
      setIsRateLimited(false);
    }
  };

  // Record a new send attempt
  const recordSendAttempt = () => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const now = Date.now();
      
      let data: RateLimitStorageData = {
        lastSentAt: now,
        dailySendCount: 1,
        lastSentDate: currentDate
      };
      
      const storedData = localStorage.getItem(storageKey);
      if (storedData) {
        const parsedData: RateLimitStorageData = JSON.parse(storedData);
        
        // If it's the same day, increment the counter; otherwise, reset
        if (parsedData.lastSentDate === currentDate) {
          data.dailySendCount = parsedData.dailySendCount + 1;
        }
      }
      
      localStorage.setItem(storageKey, JSON.stringify(data));
      
      // Update state
      setRemainingAttempts(Math.max(0, DAILY_LIMIT - data.dailySendCount));
      setIsLimitReached(data.dailySendCount >= DAILY_LIMIT);
      setRemainingTime(COOLDOWN_MINUTES * 60);
      setIsRateLimited(true);
    } catch (error) {
      console.error("Error recording send attempt:", error);
    }
  };

  // Effect for countdown timer
  useEffect(() => {
    loadRateLimitData();
    
    // Set up countdown timer if rate limited
    let timer: number | undefined;
    if (remainingTime > 0) {
      timer = window.setInterval(() => {
        setRemainingTime(prevTime => {
          const newTime = prevTime - 1;
          if (newTime <= 0) {
            clearInterval(timer);
            setIsRateLimited(false);
            loadRateLimitData(); // Refresh data when countdown ends
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, []);

  // Format remaining time into minutes and seconds
  const formatRemainingTime = () => {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    
    return {
      minutes,
      seconds,
      formatted: `${minutes}:${seconds.toString().padStart(2, '0')}`
    };
  };

  // Get status message based on current state
  const getStatusMessage = (): string | null => {
    if (isLimitReached) {
      return t('auth.dailyLimitReached');
    }
    
    if (isRateLimited) {
      const time = formatRemainingTime();
      if (time.minutes > 0) {
        return t('auth.pleaseWaitMinutes', { minutes: time.minutes });
      } else {
        return t('auth.pleaseWaitSeconds', { seconds: time.seconds });
      }
    }
    
    return null;
  };

  return {
    isRateLimited,
    isLimitReached,
    remainingTime,
    remainingAttempts,
    recordSendAttempt,
    getStatusMessage,
    formatRemainingTime,
    DAILY_LIMIT
  };
}
